import React, { useState, useRef, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import uuidv4 from "uuid/v4";
import { useTranslation } from "react-i18next";
import { Envelope, Download, Send } from "react-bootstrap-icons";
import { BallPrint } from "../ball/BallPrint";
import jsPDF from "jspdf";
import { connect } from "react-redux";
import html2canvas from "html2canvas";
import moment from "moment";
import {
  specSheetSendEmail,
  specSheetShareToStats,
  specSheetFetchStatsShares,
  setShowSharingModal,
} from "../specsheet/specsheet.redux";

const doubleButtonStyle = {
  width: "150px",
  height: "100%",
  border: "1px solid var(--unnamed-color-d4d4d5)",
  borderRadius: "10px",
  background: "#00197B 0% 0% no-repeat padding-box",
  border: "1px solid #D4D4D5",
  opacity: "1",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  margin: "5px",
};

const logoStyle = {
  width: "20px",
  height: "20px",
  padding: "0",
  margin: "0 5px 0 0",
  cursosor: "pointer",
};

const ModalSharingRedux = (props) => {
  const {
    specSheetSendEmail,
    specSheetShareToStats,
    setShowSharingModal,
    showSharingModal,
    currentSpecsheet,
    userEmail,
    proshopName,
    proshopPhone,
    sharingToStats,
    specSheetFetchStatsShares,
    statsShares,
  } = props;
  const { t } = useTranslation();

  const [showErrorMessage, setShowErrorMessage] = useState(false);
  // One id per opening of the modal: a retried click (lost response, timeout)
  // reuses it, and the server treats it as the same share instead of queueing
  // a second event for the customer.
  const [shareOperationId, setShareOperationId] = useState(null);
  useEffect(() => {
    if (showSharingModal) setShareOperationId(uuidv4());
  }, [showSharingModal]);

  // Status of the last Spectre Stats share of this sheet (Fase 1b: the
  // customer accepting it in the app comes back as ball.share.accepted).
  const specSheetId = currentSpecsheet?.id;
  useEffect(() => {
    if (showSharingModal && specSheetId) {
      specSheetFetchStatsShares({ specSheetId });
    }
  }, [showSharingModal, specSheetId, specSheetFetchStatsShares]);
  const lastStatsShare = statsShares && statsShares.length > 0 ? statsShares[0] : null;
  const statsShareStatusText = (share) => {
    const date = (value) => (value ? moment(value).format("L") : "");
    switch (share.status) {
      case "ACCEPTED":
        return t("form.label.stats_share_accepted", {
          date: date(share.acceptedAt),
          defaultValue: "Accepted by the customer in Spectre Stats on {{date}}",
        });
      case "DELIVERED":
        return t("form.label.stats_share_delivered", {
          date: date(share.deliveredAt || share.sharedAt),
          defaultValue: "Delivered to Spectre Stats on {{date}}; waiting for the customer to accept it",
        });
      case "FAILED":
        return t("form.label.stats_share_failed", {
          date: date(share.sharedAt),
          defaultValue: "The share of {{date}} could not reach Spectre Stats. Send it again.",
        });
      default:
        return t("form.label.stats_share_queued", {
          date: date(share.sharedAt),
          defaultValue: "Sent on {{date}}; Spectre Stats will receive it shortly",
        });
    }
  };

  const printPageRef = useRef();

  const blobToBase64 = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result.split(",")[1]); // Extract Base64 content
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  const generatePDF = async () => {
    if (!printPageRef.current) return;
    try {
      const canvas = await html2canvas(printPageRef.current, {
        scale: 2, // Reduced from 4 - still good quality but much smaller file
        useCORS: true,
        width: printPageRef.current.scrollWidth,
        height: printPageRef.current.scrollHeight,
      });

      // Use JPEG with compression instead of PNG for smaller file size
      const imgData = canvas.toDataURL("image/jpeg", 0.85);

      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;
      const pdf = new jsPDF("p", "pt", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const ratio = Math.min(pdfWidth / canvasWidth, pdfHeight / canvasHeight);

      const imgWidth = canvasWidth * ratio;
      const imgHeight = canvasHeight * ratio;

      const fileName = `Specsheet-${currentSpecsheet.id}.pdf`;

      pdf.addImage(
        imgData,
        "JPEG",
        0,
        0,
        imgWidth,
        imgHeight,
        undefined,
        "FAST"
      );
      pdf.save(fileName);
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

  const sendPDFEmail = async () => {
    if (!printPageRef.current) return;
    try {
      const canvas = await html2canvas(printPageRef.current, {
        scale: 1,
        useCORS: true,
        width: printPageRef.current.scrollWidth,
        height: printPageRef.current.scrollHeight,
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.85);

      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;
      const pdf = new jsPDF("p", "pt", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const ratio = Math.min(pdfWidth / canvasWidth, pdfHeight / canvasHeight);

      const imgWidth = canvasWidth * ratio;
      const imgHeight = canvasHeight * ratio;

      const fileName = `Specsheet-${currentSpecsheet.id}.pdf`;

      pdf.addImage(
        imgData,
        "PNG",
        0,
        0,
        imgWidth,
        imgHeight,
        undefined,
        "FAST"
      );

      // Generate the PDF blob
      const blob = pdf.output("blob");
      const base64File = await blobToBase64(blob);

      if (!blob) {
        throw new Error("Failed to generate PDF blob.");
      }

      // Dispatch the file name to Redux
      specSheetSendEmail({
        email: userEmail,
        fileName: fileName,
        file: base64File,
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

  return (
    <>
      <Modal
        show={showSharingModal}
        onHide={() => {
          setShowSharingModal({ showSharingModal: false });
        }}
        size="sm"
        aria-labelledby="contained-modal-title-vcenter"
        dialogClassName="confirmDeleteArsenal-popup"
        centered
      >
        <Modal.Header closeButton>
          <div style={{ marginTop: 10, lineHeight: 1.2 }}>
            <span
              style={{
                fontSize: 16,
                fontFamily: "Poppins",
                fontWeight: "bold",
                textTransform: "uppercase",
              }}
            >
              {t("form.label.share_specsheet")}
            </span>
          </div>
        </Modal.Header>
        <Modal.Body
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <span
            style={{
              display: showErrorMessage && !proshopName ? "" : "none",
              color: "red",
            }}
          >
            {t("form.label.error_enter_name_for_pro_shop")}!!!
          </span>
          <span
            style={{
              display: showErrorMessage && !userEmail ? "" : "none",
              color: "red",
            }}
          >
            {t("form.label.error_enter_email_for_customer")}!!!
          </span>
          <span
            style={{
              display: showErrorMessage && !proshopPhone ? "" : "none",
              color: "red",
            }}
          >
            {t("form.label.error_enter_phone_for_pro_shop")}!!!
          </span>
          <Button
            style={doubleButtonStyle}
            onClick={async () => {
              try {
                if (proshopName && userEmail && proshopPhone) {
                  sendPDFEmail();
                  setShowErrorMessage(false);
                  setShowSharingModal({ showSharingModal: false });
                } else {
                  setShowErrorMessage(true);
                }
              } catch (error) {
                console.error("Error:", error);
              }
            }}
          >
            <Envelope style={logoStyle} />
            {t("form.label.email")}
          </Button>
          <Button
            style={doubleButtonStyle}
            onClick={async () => {
              try {
                generatePDF();
                setShowSharingModal({ showSharingModal: false });
                setShowErrorMessage(false);
              } catch (error) {
                console.error("Error:", error);
              }
            }}
          >
            <Download style={logoStyle} />
            {t("form.label.download")}
          </Button>
          {currentSpecsheet?.id && (
            <Button
              style={doubleButtonStyle}
              disabled={sharingToStats}
              onClick={() => {
                if (!userEmail) {
                  setShowErrorMessage(true);
                  return;
                }
                setShowErrorMessage(false);
                // The modal stays open (button disabled) until the epic
                // reports success or a specific error.
                specSheetShareToStats({
                  specSheetId: currentSpecsheet.id,
                  specSheet: currentSpecsheet,
                  operationId: shareOperationId || uuidv4(),
                });
              }}
            >
              {sharingToStats ? (
                <Spinner animation="border" size="sm" style={logoStyle} />
              ) : (
                <Send style={logoStyle} />
              )}
              {t("form.label.send_to_spectre_stats")}
            </Button>
          )}
          {currentSpecsheet?.id && lastStatsShare && (
            <span
              style={{
                fontSize: 12,
                textAlign: "center",
                marginTop: 4,
                maxWidth: 220,
                color: lastStatsShare.status === "FAILED" ? "red" : "#4A5866",
              }}
            >
              {statsShareStatusText(lastStatsShare)}
            </span>
          )}
        </Modal.Body>
      </Modal>
      <div
        style={{
          position: "absolute",
          top: "-9999px",
          left: "-9999px",
          width: "auto",
          height: "auto",
          overflow: "visible",
        }}
      >
        <div ref={printPageRef}>
          <BallPrint />
        </div>
      </div>
    </>
  );
};

export const ModalSharing = connect(
  (state) => {
    return {
      currentSpecsheet: state.specsheet.currentSpecSheet,
      userEmail: state.bowlers.selectedCustomer?.email,
      showSharingModal: state.specsheet.showSharingModal,
      proshopName: state.userSession.proshop?.name,
      proshopPhone: state.userSession.userData?.phone,
      sharingToStats: state.specsheet.sharingToStats,
      statsShares: state.specsheet.statsShares,
    };
  },
  {
    specSheetSendEmail,
    specSheetShareToStats,
    specSheetFetchStatsShares,
    setShowSharingModal,
  }
)(ModalSharingRedux);
